import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, Briefcase, SlidersHorizontal, Target, Check, ExternalLink } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import TestimonialVideoPlayer from "@/components/landing/TestimonialVideoPlayer";
import { TESTIMONIALS } from "@/data/testimonials";

const WORK_CARDS = [
  {
    icon: Briefcase,
    title: "Experiencia ejecutiva real",
    text: "He estado del otro lado de la mesa. Entiendo la presión de tener clientes, equipos, objetivos y múltiples prioridades ocurriendo al mismo tiempo. Por eso busco que las herramientas funcionen en el mundo real, no solamente en una presentación.",
  },
  {
    icon: SlidersHorizontal,
    title: "Implementación 100% pragmática",
    text: "Una empresa que crece inevitablemente se vuelve más compleja. El objetivo no es agregar más burocracia, sino simplificar, clarificar responsabilidades y construir una forma más disciplinada de operar.",
  },
  {
    icon: Target,
    title: "Lo implemento yo mismo, en tu empresa",
    text: "Asumo personalmente el desafío de entrenarte a ti y a tu equipo de liderazgo en las prácticas de EOS. No es una consultoría tradicional ni delego el proceso en terceros: trabajo directamente con ustedes de principio a fin.",
  },
];

const SIGNALS = [
  "Demasiadas decisiones siguen dependiendo del dueño o gerente general.",
  "El equipo de liderazgo no siempre tiene las mismas prioridades.",
  "Existen muchas iniciativas abiertas y cuesta terminarlas.",
  "Las reuniones ocupan mucho tiempo pero resuelven poco.",
  "Hay responsabilidades poco claras.",
  "Los mismos problemas aparecen una y otra vez.",
  "Existe una estrategia, pero cuesta convertirla en ejecución.",
  "La empresa crece y con ese crecimiento también aumenta la complejidad.",
];

export default function ImplementadorEOS() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "Carlos Garcín - Implementador Profesional EOS",
      description:
        "Qué hace un Implementador EOS y cómo Carlos Garcín, Professional EOS Implementer® en Chile y Latinoamérica, ayuda a empresarios a escalar con EOS®.",
      path: "/implementador-eos",
      keywords:
        "Implementador EOS, Professional EOS Implementer, implementador EOS Chile, qué hace un implementador EOS, Carlos Garcín, EOS Latinoamérica, consultor EOS",
      image: "https://garcin.cl/implementador-eos/hero-real-simple-resultados.jpg",
    });
    window.scrollTo(0, 0);
  }, []);

  const testimonial = TESTIMONIALS[0];
  const featuredQuote = testimonial.quotes.find((q) =>
    q.includes("Recomiendo a Carlos Garcín")
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: "https://garcin.cl/" },
      { "@type": "ListItem", position: 2, name: "Implementador EOS", item: "https://garcin.cl/implementador-eos" },
    ],
  };

  const profilePageSchema = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "@id": "https://garcin.cl/implementador-eos#profilepage",
    url: "https://garcin.cl/implementador-eos",
    name: "Carlos Garcín - Implementador Profesional EOS",
    description:
      "Qué hace un Implementador EOS y cómo Carlos Garcín, Professional EOS Implementer® en Chile y Latinoamérica, ayuda a empresarios a escalar con EOS®.",
    inLanguage: "es-CL",
    datePublished: "2026-09-30T09:00:00-03:00",
    dateModified: "2026-09-30T09:00:00-03:00",
    mainEntity: {
      "@type": "Person",
      "@id": "https://garcin.cl/#carlos-garcin",
      name: "Carlos Garcín",
      jobTitle: "Professional EOS Implementer®",
      description:
        "Implementador Profesional EOS certificado por EOS Worldwide. Ayuda a empresarios y equipos de liderazgo en Chile y Latinoamérica a construir empresas con visión compartida, disciplina de ejecución y equipos más saludables.",
      url: "https://garcin.cl/implementador-eos",
      image: "https://garcin.cl/implementador-eos/hero-real-simple-resultados.jpg",
      email: "carlos.garcin@eosworldwide.com",
      telephone: "+56 9 9917 6009",
      areaServed: ["Chile", "Latinoamérica"],
      sameAs: [
        "https://implementer.eosworldwide.com/carlos-garcin/",
        "https://www.linkedin.com/in/cgarcin-cl/",
        "https://www.instagram.com/implementador_eos",
        "https://www.youtube.com/@implementador_eos",
      ],
    },
  };

  const mainVideoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": "https://garcin.cl/implementador-eos#video-principal",
    name: "Te cuento qué es EOS en menos de 2 minutos ⏳",
    description:
      "Carlos Garcín cuenta brevemente qué hace como Professional EOS Implementer y cómo ayuda a los equipos de liderazgo a fortalecer la Visión, la Tracción y la Salud de su empresa.",
    uploadDate: "2026-07-29T10:15:14-07:00",
    thumbnailUrl: "https://i.ytimg.com/vi/EYwWG_I8v0I/maxresdefault.jpg",
    embedUrl: "https://www.youtube.com/embed/EYwWG_I8v0I",
    contentUrl: "https://www.youtube.com/watch?v=EYwWG_I8v0I",
    duration: "PT1M45S",
  };

  const testimonialVideoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": "https://garcin.cl/implementador-eos#video-testimonio",
    name: "Testimonio: ¿Cuáles son los resultados concretos de una empresa operando bajo EOS?",
    description:
      "Testimonio de Álvaro Añón, Founder & CEO de Soho.lat, sobre los resultados de implementar EOS con Carlos Garcín.",
    uploadDate: "2026-08-11T08:49:52-07:00",
    thumbnailUrl: "https://i.ytimg.com/vi/79eYtP9iRr4/maxresdefault.jpg",
    embedUrl: "https://www.youtube.com/embed/79eYtP9iRr4",
    contentUrl: "https://www.youtube.com/watch?v=79eYtP9iRr4",
    duration: "PT1M11S",
  };

  return (
    <>
      <Header />
      <main className="implementador-main">
        {/* 1. HERO */}
        <section className="bg-[#0A1128] pt-28 pb-16" data-testid="implementador-hero">
          <div className="container-eos">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-7">
                <p className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#F26522]">
                  Professional EOS Implementer®
                </p>
                <h1 className="mt-4 font-display font-extrabold text-white leading-[1.05] tracking-[-0.02em] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
                  Carlos Garcín - <span className="text-[#F26522]">Implementador Profesional EOS</span>
                </h1>
                <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed">
                  Soy Carlos Garcín, Implementador Profesional EOS. Ayudo a empresarios y equipos de
                  liderazgo en Chile y Latinoamérica a construir empresas con una visión compartida,
                  mayor disciplina de ejecución y equipos más saludables.
                </p>
                <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed">
                  Implemento EOS®, un sistema de gestión simple y práctico que ayuda a transformar la
                  visión del negocio en prioridades, responsables, números y acciones concretas.
                </p>
                <p className="mt-6 text-xs sm:text-sm font-semibold tracking-[0.08em] uppercase text-slate-400">
                  Professional EOS Implementer® · Chile y Latinoamérica · Sesiones presenciales y virtuales
                </p>
                <Link
                  to="/contacto"
                  data-testid="implementador-hero-cta"
                  className="mt-8 inline-flex items-center gap-2 bg-[#F26522] text-white font-bold uppercase tracking-[0.08em] text-sm px-6 py-3.5 rounded-md shadow-lg shadow-[#F26522]/20 hover:bg-[#D95214] hover:-translate-y-0.5 transition-all"
                >
                  Conversemos sobre tu empresa
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="lg:col-span-5">
                <img
                  src="/implementador-eos/hero-real-simple-resultados.jpg"
                  alt="Carlos Garcín, Professional EOS Implementer — Real. Simple. Resultados."
                  data-testid="implementador-hero-image"
                  className="w-full max-w-md mx-auto lg:max-w-none rounded-2xl ring-1 ring-white/10 shadow-[0_25px_60px_-20px_rgba(10,17,40,0.35)]"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 2. QUÉ HACE UN IMPLEMENTADOR EOS */}
        <section className="section-padding bg-white" data-testid="implementador-que-hace">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">El rol</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-navy-800 leading-tight">
                ¿Qué hace un <span className="text-[#F26522]">Implementador EOS</span>?
              </h2>
              <div className="mt-6 space-y-5 text-slate-600 text-base sm:text-lg leading-relaxed">
                <p>
                  Un Implementador EOS ayuda al equipo de liderazgo de una empresa a{" "}
                  <strong className="font-semibold text-navy-800">aprender, implementar y dominar
                  las herramientas de EOS</strong> — Entrepreneurial Operating System®.
                </p>
                <p>
                  Mi trabajo no consiste en llegar con las respuestas ni decirle al empresario cuál
                  debe ser su estrategia. Trabajo con el equipo como{" "}
                  <strong className="font-semibold text-navy-800">profesor, facilitador y coach</strong>.
                </p>
                <p>
                  Como profesor, <strong className="font-semibold text-navy-800">enseño herramientas
                  simples y prácticas</strong>. Como facilitador, ayudo al equipo a tener las
                  conversaciones que necesita tener y{" "}
                  <strong className="font-semibold text-navy-800">encontrar sus propias respuestas</strong>.
                  Y como coach, acompaño el proceso,{" "}
                  <strong className="font-semibold text-navy-800">hago las preguntas difíciles</strong>{" "}
                  cuando corresponde y ayudo a{" "}
                  <strong className="font-semibold text-navy-800">mantener la disciplina</strong>{" "}
                  necesaria para avanzar.
                </p>
                <p>
                  El objetivo es que EOS termine siendo{" "}
                  <strong className="font-semibold text-navy-800">parte de la forma en que la empresa
                  se gestiona</strong>, sin depender permanentemente del implementador.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 3. VIDEO */}
        <section className="section-padding bg-slate-50" data-testid="implementador-video-section">
          <div className="container-eos">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-4 reveal">
                <TestimonialVideoPlayer videoId="EYwWG_I8v0I" name="Carlos Garcín" />
              </div>
              <div className="lg:col-span-8 reveal">
                <span className="eyebrow">Video</span>
                <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-navy-800 leading-tight">
                  ¿Qué es EOS y cómo puedo <span className="text-[#F26522]">ayudarte</span>?
                </h2>
                <p className="mt-6 text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl">
                  En este video te cuento brevemente qué es EOS, qué tipo de problemas busca
                  resolver y cuál es mi rol como Implementador EOS trabajando con empresarios y
                  equipos de liderazgo.
                </p>
                <p className="mt-5 text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl">
                  EOS trabaja sobre seis componentes fundamentales de una empresa: <strong className="text-navy-800">Visión, Personas, Datos, Asuntos, Procesos y Tracción</strong>.
                  Al fortalecer estos componentes, la organización gana claridad, disciplina y una
                  forma más consistente de ejecutar.
                </p>
                <div className="mt-6 flex justify-end">
                  <Link
                    to="/contacto"
                    data-testid="implementador-cta-video"
                    className="btn-primary"
                  >
                    Agendar una llamada
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. MI HISTORIA */}
        <section className="section-padding bg-white" data-testid="implementador-historia">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">Mi historia</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-navy-800 leading-tight">
                Antes de implementar EOS para otros,{" "}
                <span className="text-[#F26522]">lo viví desde dentro</span>
              </h2>
              <div className="mt-6 space-y-5 text-slate-600 text-base sm:text-lg leading-relaxed">
                <p className="font-semibold text-navy-800">
                  Llegué a EOS buscando soluciones a los mismos problemas que te enfrentas tú.
                </p>
                <p>
                  Durante <strong className="font-semibold text-navy-800">más de 20 años</strong> he
                  trabajado liderando operaciones, tecnología, procesos y equipos. Como ejecutivo
                  también viví muchos de los problemas que hoy encuentro en las empresas con las que
                  trabajo: demasiadas prioridades, reuniones poco efectivas, responsabilidades poco
                  claras, decisiones concentradas en pocas personas y una estrategia que no siempre
                  logra transformarse en ejecución.{" "}
                  <strong className="font-semibold text-navy-800">Esa sensación que estás tocando el
                  techo para seguir creciendo con tu negocio.</strong>
                </p>
                <p>
                  <strong className="font-semibold text-navy-800">Primero implementé EOS desde dentro
                  de la empresa.</strong> Viví directamente sus herramientas, sus reuniones, las{" "}
                  <strong className="font-semibold text-navy-800">rocas trimestrales</strong>, el{" "}
                  <strong className="font-semibold text-navy-800">Scorecard</strong>, el{" "}
                  <strong className="font-semibold text-navy-800">Accountability Chart</strong> y el
                  trabajo necesario para alinear a un equipo de liderazgo.
                </p>
                <p>
                  Eso fue importante para mí porque antes de convertirme en Implementador EOS estuve{" "}
                  <strong className="font-semibold text-navy-800">sentado al otro lado de la mesa
                  como ejecutivo y emprendedor</strong>.
                </p>
                <p>
                  Sé lo que significa tener que cumplir metas, gestionar clientes, liderar personas,
                  resolver problemas operativos y al mismo tiempo intentar{" "}
                  <strong className="font-semibold text-navy-800">construir una empresa que pueda
                  seguir creciendo</strong>.
                </p>
                <p>
                  Esa experiencia fue la que finalmente me llevó a convertirme en{" "}
                  <strong className="font-semibold text-navy-800">Professional EOS Implementer®
                  certificado por EOS Worldwide</strong>.
                </p>
              </div>
              <a
                href="https://implementer.eosworldwide.com/carlos-garcin/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="implementador-perfil-eos-button"
                className="mt-8 inline-flex items-center gap-2 border-2 border-navy-800 text-navy-800 font-bold uppercase tracking-[0.08em] text-sm px-6 py-3.5 rounded-md hover:bg-navy-800 hover:text-white transition-all"
              >
                Ver mi perfil oficial en EOS Worldwide
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* 5. MI FORMA DE TRABAJAR */}
        <section className="section-padding bg-slate-50" data-testid="implementador-forma-trabajar">
          <div className="container-eos">
            <div className="reveal">
              <span className="eyebrow">Mi forma de trabajar</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-navy-800 leading-tight">
                Mi forma de trabajar como{" "}
                <span className="text-[#F26522]">Implementador EOS</span>
              </h2>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {WORK_CARDS.map((c, i) => (
                <article
                  key={i}
                  data-testid={`implementador-work-card-${i + 1}`}
                  className="reveal bg-navy-800 border-t-[3px] border-[#F26522] rounded-2xl p-8 flex flex-col"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#F26522] flex items-center justify-center shrink-0 shadow-lg shadow-[#F26522]/40">
                      <c.icon className="w-5 h-5 text-white" strokeWidth={2} />
                    </div>
                    <h3 className="font-display font-bold text-white text-xl leading-snug">
                      {c.title}
                    </h3>
                  </div>
                  <p className="mt-4 text-slate-400 text-base leading-relaxed">{c.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* 6. TESTIMONIO */}
        <section className="section-padding bg-white" data-testid="implementador-testimonio">
          <div className="container-eos">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-4 reveal">
                <TestimonialVideoPlayer
                  videoId={testimonial.videoId}
                  posterImage={testimonial.poster}
                  name={testimonial.name}
                />
              </div>
              <div className="lg:col-span-8 reveal">
                <span className="eyebrow">Testimonio</span>
                <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-navy-800 leading-tight">
                  ¿Cómo se vive una implementación EOS{" "}
                  <span className="text-[#F26522]">desde dentro</span>?
                </h2>
                <p className="mt-6 text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl">
                  Más allá de explicar qué hago como Implementador EOS, prefiero que lo cuente
                  alguien que vivió el proceso desde dentro de su propia empresa.
                </p>
                <blockquote
                  data-testid="implementador-testimonio-quote"
                  className="mt-8 bg-slate-50 border-l-4 border-[#F26522] rounded-r-2xl p-6 sm:p-8 flex gap-3"
                >
                  <Quote className="w-6 h-6 text-[#F26522] shrink-0 mt-1" fill="currentColor" />
                  <p className="text-navy-800 text-xl sm:text-2xl font-display font-bold leading-snug italic">
                    “{featuredQuote}”
                  </p>
                </blockquote>
                <p className="mt-6 font-display font-bold text-xl text-navy-800" data-testid="implementador-testimonio-name">
                  {testimonial.name}
                </p>
                <p className="mt-1 text-sm sm:text-base text-slate-500">
                  {testimonial.role}
                  {" "}·{" "}
                  <span className="text-[#F26522] font-semibold" data-testid="implementador-testimonio-company">
                    {testimonial.company}
                  </span>
                </p>
                <div className="mt-6 flex justify-end">
                  <Link
                    to="/contacto"
                    data-testid="implementador-cta-testimonio"
                    className="inline-flex items-center gap-1.5 text-[#F26522] font-semibold hover:underline"
                  >
                    Hablemos, sin compromiso →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 7. ¿PARA QUÉ EMPRESAS PUEDE TENER SENTIDO? */}
        <section className="section-padding bg-slate-50" data-testid="implementador-senales">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">¿Es para tu empresa?</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-navy-800 leading-tight">
                ¿Cuándo puede hacer sentido trabajar con un{" "}
                <span className="text-[#F26522]">Implementador EOS</span>?
              </h2>
              <p className="mt-6 text-slate-600 text-base sm:text-lg leading-relaxed">
                EOS suele ser especialmente útil para empresas que están creciendo y comienzan a
                sentir que su forma actual de operar ya no alcanza.
              </p>
              <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed font-semibold text-navy-800">
                Algunas señales frecuentes:
              </p>
            </div>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
              {SIGNALS.map((s, i) => (
                <li
                  key={i}
                  data-testid={`implementador-senal-${i + 1}`}
                  className="reveal flex items-start gap-3 bg-white rounded-xl p-4 ring-1 ring-slate-200"
                >
                  <div className="w-6 h-6 rounded-full bg-[#F26522]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-[#F26522]" strokeWidth={3} />
                  </div>
                  <span className="text-slate-700 text-base leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 max-w-3xl reveal">
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                No es necesario tener todos estos problemas.
              </p>
              <p className="mt-3 text-slate-600 text-base sm:text-lg leading-relaxed">
                Muchas veces basta con que el equipo sienta algo mucho más simple:
              </p>
              <p className="mt-4 text-navy-800 text-lg sm:text-xl font-display font-bold italic">
                “Sabemos que podríamos estar operando mejor de lo que estamos operando hoy.”
              </p>
            </div>
          </div>
        </section>

        {/* 7.5 RED EJECUTIVA */}
        <section className="section-padding bg-white" data-testid="implementador-red">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">Red de confianza</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-navy-800 leading-tight">
                Una red ejecutiva <span className="text-[#F26522]">más allá de EOS</span>
              </h2>
              <div className="mt-6 space-y-5 text-slate-600 text-base sm:text-lg leading-relaxed">
                <p>
                  Mi foco es implementar EOS y ayudarte a fortalecer la forma en que opera tu
                  empresa. Pero en el camino pueden aparecer desafíos que requieren experiencia
                  específica en otras áreas.
                </p>
                <p>
                  Cuando eso ocurre, tengo acceso a una{" "}
                  <strong className="font-semibold text-navy-800">red de ejecutivos y especialistas
                  de confianza</strong>, con experiencia en áreas como:
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5" data-testid="implementador-red-areas">
                {["Estrategia", "Operaciones", "Comercial", "Marketing", "Finanzas", "Personas", "Tecnología"].map((area) => (
                  <span
                    key={area}
                    className="inline-flex items-center px-4 py-1.5 rounded-full border border-slate-200 bg-slate-50 text-sm font-semibold text-navy-800"
                  >
                    {area}
                  </span>
                ))}
              </div>
              <p className="mt-6 text-slate-600 text-base sm:text-lg leading-relaxed">
                No pretendo tener todas las respuestas. Si un desafío requiere otra especialidad,{" "}
                <strong className="font-semibold text-navy-800">sé a quién llamar y cómo conectarte
                con la experiencia adecuada</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* 8. CIERRE / CTA */}
        <section
          className="section-padding bg-navy-800 text-white relative overflow-hidden"
          data-testid="implementador-final-cta"
        >
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,1) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#F26522] opacity-10 blur-3xl pointer-events-none" />
          <div className="container-eos relative">
            <div className="max-w-3xl reveal">
              <span className="inline-block text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-[#F26522]">
                Próximo paso
              </span>
              <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-tight text-white">
                ¿Quieres saber si EOS puede ayudar a{" "}
                <span className="text-[#F26522]">tu empresa</span>?
              </h2>
              <p className="mt-5 text-lg text-slate-300 leading-relaxed">
                Conversemos sobre qué están tratando de lograr, cuáles son los principales desafíos
                que están enfrentando y si EOS puede ayudarlos a construir una organización más
                clara, disciplinada y saludable.
              </p>
              <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                Si vemos que existe un posible calce, te explicaré cómo funciona el proceso y cuáles
                podrían ser los siguientes pasos.
              </p>
            </div>
            <div className="mt-10 reveal">
              <Link
                to="/contacto"
                data-testid="implementador-final-cta-button"
                className="btn-primary"
              >
                Conversemos sobre tu empresa
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(profilePageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(mainVideoSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(testimonialVideoSchema) }} />
    </>
  );
}
